import React from "react";

// --- icon ---
import { ArrowDown, ArrowUp, LoaderCircle } from "lucide-react";

type Column<T> = {
    header: string;
    accessor: keyof T | ((row: T) => React.ReactNode) | React.ReactNode | any;
    sortable?: boolean;
    sortBy?: string;
    className?: string;
    hidden?: boolean;
};

type TableProps<T> = {
    columns: Column<T>[];
    data: T[];
    isLoading?: boolean;
    noDataText?: string;
    sortBy?: string;
    order?: "asc" | "desc";
    handleSort?: (column: string) => void;
};

export default function ListTable<T extends object>({
    columns,
    data,
    isLoading = false,
    noDataText = "Không có dữ liệu",
    sortBy,
    order,
    handleSort,
}: TableProps<T>) {

    return (
        <div className="relative w-full h-full overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
            <table className="min-w-full text-sm text-left text-gray-700">
                <thead className="sticky top-[-1px] z-[1] bg-gray-200 text-sm font-semibold text-gray-600">
                    <tr>
                        {columns.map((column, index) => (
                            <th
                                key={index}
                                hidden={column.hidden}
                                className={`${column.className} first:px-2 last:pr-2 px-4 py-3 text-left cursor-pointer`}
                                onClick={() => column.sortable && column.sortBy && handleSort && handleSort(column.sortBy)}
                            >
                                <div className="flex items-center justify-between">
                                    <span>{column.header}</span>
                                    <div className={`${(column.sortable && column.sortBy) ? 'block' : 'hidden'} ${sortBy === column.sortBy ? 'opacity-100' : 'opacity-30'}`}>
                                        {order === "asc" ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                                    </div>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="px-4 py-4 text-center text-gray-500">
                                {noDataText}
                            </td>
                        </tr>
                    ) : (
                        data.map((row, rowIndex) => {
                            const index = rowIndex + 1;
                            return (
                                <tr key={rowIndex} className={`hover:bg-gray-100 border-b ${rowIndex % 2 === 1 ? "bg-gray-50" : ""}`}>
                                    {columns.map((column, colIndex) => (
                                        <td
                                            key={colIndex}
                                            hidden={column.hidden}
                                            className={`${column.accessor === "index" ? 'text-center' : ''} px-2 py-2`}
                                        >
                                            {column.accessor === "index"
                                                ? index
                                                : typeof column.accessor === "function"
                                                    ? column.accessor(row)
                                                    : row[column.accessor as keyof T] || "-"}
                                        </td>
                                    ))}
                                </tr>
                            )
                        })
                    )}
                </tbody>
            </table>
            {isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-70 flex flex-col items-center justify-center z-10">
                    <LoaderCircle className="animate-spin text-blue-500 mb-2" size={36} />
                    <span className="text-gray-700 font-medium">Đang tải dữ liệu...</span>
                </div>
            )}
        </div>
    );
}
